import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  createBoard,
  deleteBoard,
  fetchBoards,
  getBoardsSortedByName,
  getTournoi,
  updateBoardDefinition,
  syncBoardIncompatibilities,
  type BoardDefinitionCreateInput,
  type BoardDefinitionUpdateInput,
} from "../../../features/tournoi/tournoiSlice";
import { showNotification } from "../../../features/globalNotification/globalNotificationSlice";
import { Tableau } from "../../../types";
import ConfirmDialog from "../../Base/ConfirmDialog";
import {
  applyCategoryGroupToggle,
  BOARD_CATEGORY_GROUPS,
  BOARD_CATEGORY_OPTIONS,
  BOARD_SEXE_OPTIONS,
  formatBoardCategoryMenuLabel,
  getAllBoardCategoryCodes,
  getCategoryGroupCheckboxState,
  isKnownBoardCategoryCode,
  legacyBoardCategoryCodes,
  HANDISPORT_TABLE_FLAG,
} from "../../../constants/boardDefinitions";
import {
  isValidHexColor,
  normalizeBoardLiveColor,
} from "../../../utils/boardLiveColor";

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDateInput(s: string): Date {
  const [y, mo, da] = s.split("-").map((x) => parseInt(x, 10));
  if (!y || !mo || !da) return new Date();
  return new Date(y, mo - 1, da, 12, 0, 0, 0);
}

type DialogMode = "create" | "edit" | null;

type FormState = {
  name: string;
  label: string;
  heureLimite: string;
  dateStr: string;
  nbPlace: string;
  realNbPlaces: string;
  sexe: string[];
  cat: string[];
  handiReserve: boolean;
  classe: string[];
  min: string;
  max: string;
  obligatoire: boolean;
  fullPrice: boolean;
  /** Si false, n’est pas décompté dans la limite de 2 tableaux / jour. */
  comptePourLimiteJournaliere: boolean;
  /** Info « inscription sur place » sur la page de saisie de licence. */
  inscriptionSurPlace: boolean;
  /** Exclu de la grille tarifaire (gratuit). */
  offert: boolean;
  incompatibleBoardIds: string[];
  liveColor: string;
};

const emptyForm = (): FormState => ({
  name: "",
  label: "",
  heureLimite: "18:00",
  dateStr: toDateInputValue(new Date()),
  nbPlace: "16",
  realNbPlaces: "16",
  sexe: ["M", "F"],
  cat: [],
  handiReserve: false,
  classe: [],
  min: "",
  max: "",
  obligatoire: false,
  fullPrice: false,
  comptePourLimiteJournaliere: true,
  inscriptionSurPlace: false,
  offert: false,
  incompatibleBoardIds: [],
  liveColor: "",
});

function boardToForm(b: Tableau): FormState {
  return {
    name: b.name,
    label: b.label,
    heureLimite: b.heureLimite,
    dateStr: toDateInputValue(b.date instanceof Date ? b.date : new Date(b.date)),
    nbPlace: String(b.nbPlace),
    realNbPlaces: String(b.realNbPlaces),
    sexe: [...b.sexe],
    cat: [...b.cat],
    handiReserve: Boolean(b.handi),
    classe: b.classe ? [...b.classe] : [],
    min: b.min !== undefined ? String(b.min) : "",
    max: b.max !== undefined ? String(b.max) : "",
    obligatoire: b.obligatoire ?? false,
    fullPrice: b.fullPrice ?? false,
    comptePourLimiteJournaliere: b.comptePourLimiteJournaliere !== false,
    inscriptionSurPlace: b.inscriptionSurPlace === true,
    offert: b.offert === true,
    incompatibleBoardIds: [...(b.incompatibleBoardIds ?? [])],
    liveColor: b.liveColor ?? "",
  };
}

const AdminBoardDefinitions: React.FC = () => {
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoardsSortedByName);
  const [searchParams, setSearchParams] = useSearchParams();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Tableau | null>(null);

  useEffect(() => {
    dispatch(fetchBoards());
  }, [dispatch, tournoi?.id]);

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogMode("create");
  }, []);

  const openEdit = useCallback((b: Tableau) => {
    setEditingId(b.id as string);
    setForm(boardToForm(b));
    setDialogMode("edit");
  }, []);

  const editingBoard = useMemo(
    () => (editingId ? boards.find((b) => b.id === editingId) : undefined),
    [boards, editingId]
  );

  /** Autres tableaux proposés pour l’incompatibilité (exclut le tableau en cours d’édition). */
  const incompatibleOptions = useMemo(() => {
    return [...boards]
      .filter((b) => b.id && b.id !== editingId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [boards, editingId]);

  const categoryCodesForMenu = useMemo(() => {
    const legacy = legacyBoardCategoryCodes(form.cat);
    return [...BOARD_CATEGORY_OPTIONS, ...legacy];
  }, [form.cat]);

  const legacyCategoryCodes = useMemo(
    () => legacyBoardCategoryCodes(form.cat),
    [form.cat]
  );

  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || boards.length === 0) return;
    const b = boards.find((x) => x.id === editId);
    if (b) {
      openEdit(b);
      setSearchParams({}, { replace: true });
    }
  }, [boards, searchParams, setSearchParams, openEdit]);

  const handleSexeChange = (event: SelectChangeEvent<string[]>) => {
    const v = event.target.value;
    setForm((f) => ({
      ...f,
      sexe: typeof v === "string" ? v.split(",") : v,
    }));
  };

  const handleCatChange = (event: SelectChangeEvent<string[]>) => {
    const v = event.target.value;
    setForm((f) => ({
      ...f,
      cat: typeof v === "string" ? v.split(",") : v,
    }));
  };

  const handleCategoryGroupToggle = (
    codes: readonly string[],
    checked: boolean
  ) => {
    setForm((f) => ({
      ...f,
      cat: applyCategoryGroupToggle(f.cat, codes, checked),
    }));
  };

  const handleIncompatibleChange = (event: SelectChangeEvent<string[]>) => {
    const v = event.target.value;
    setForm((f) => ({
      ...f,
      incompatibleBoardIds: typeof v === "string" ? v.split(",") : v,
    }));
  };

  const parseOptionalInt = (s: string): number | undefined => {
    const t = s.trim();
    if (t === "") return undefined;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? undefined : n;
  };

  const handleSubmit = async () => {
    const min = parseOptionalInt(form.min);
    const max = parseOptionalInt(form.max);
    if (min !== undefined && max !== undefined && min > max) {
      dispatch(
        showNotification({
          message: "Le minimum de points ne peut pas dépasser le maximum.",
          severity: "error",
        })
      );
      return;
    }
    if (form.sexe.length === 0 || form.cat.length === 0) {
      dispatch(
        showNotification({
          message: "Sélectionnez au moins un sexe et une catégorie.",
          severity: "error",
        })
      );
      return;
    }

    const liveColorTrimmed = form.liveColor.trim();
    if (liveColorTrimmed !== "" && !isValidHexColor(liveColorTrimmed)) {
      dispatch(
        showNotification({
          message: "Couleur live invalide : utilisez le format #RRGGBB.",
          severity: "error",
        })
      );
      return;
    }
    const liveColor = normalizeBoardLiveColor(liveColorTrimmed);

    setSubmitting(true);
    try {
      if (dialogMode === "create") {
        const nbPlace = parseInt(form.nbPlace, 10);
        const realNbPlaces = parseInt(form.realNbPlaces, 10);
        if (Number.isNaN(nbPlace) || nbPlace < 1 || Number.isNaN(realNbPlaces) || realNbPlaces < 1) {
          dispatch(
            showNotification({
              message: "Places affichées et réelles : entiers ≥ 1.",
              severity: "error",
            })
          );
          return;
        }
        const payload: BoardDefinitionCreateInput = {
          name: form.name,
          label: form.label,
          heureLimite: form.heureLimite,
          date: parseDateInput(form.dateStr),
          nbPlace,
          realNbPlaces,
          sexe: form.sexe,
          cat: form.cat,
          handi: form.handiReserve ? HANDISPORT_TABLE_FLAG : "",
          classe: form.classe.length ? form.classe : undefined,
          min,
          max,
          obligatoire: form.obligatoire,
          fullPrice: form.fullPrice,
          comptePourLimiteJournaliere: form.comptePourLimiteJournaliere,
          inscriptionSurPlace: form.inscriptionSurPlace,
          offert: form.offert,
          incompatibleBoardIds: form.incompatibleBoardIds,
          liveColor,
        };
        await dispatch(createBoard(payload)).unwrap();
        dispatch(
          showNotification({ message: "Tableau créé.", severity: "success" })
        );
      } else if (dialogMode === "edit" && editingId) {
        const data: BoardDefinitionUpdateInput = {
          name: form.name,
          label: form.label,
          heureLimite: form.heureLimite,
          date: parseDateInput(form.dateStr),
          sexe: form.sexe,
          cat: form.cat,
          handi: form.handiReserve ? HANDISPORT_TABLE_FLAG : "",
          classe: form.classe.length ? form.classe : undefined,
          min,
          max,
          obligatoire: form.obligatoire,
          fullPrice: form.fullPrice,
          comptePourLimiteJournaliere: form.comptePourLimiteJournaliere,
          inscriptionSurPlace: form.inscriptionSurPlace,
          offert: form.offert,
          liveColor,
        };
        await dispatch(
          updateBoardDefinition({ boardId: editingId, data })
        ).unwrap();
        await dispatch(
          syncBoardIncompatibilities({
            boardId: editingId,
            incompatibleBoardIds: form.incompatibleBoardIds,
          })
        ).unwrap();
        dispatch(
          showNotification({
            message: "Définition du tableau mise à jour.",
            severity: "success",
          })
        );
      }
      setDialogMode(null);
      setEditingId(null);
    } catch {
      dispatch(
        showNotification({
          message: "Une erreur est survenue.",
          severity: "error",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await dispatch(deleteBoard({ boardId: deleteTarget.id as string })).unwrap();
      dispatch(
        showNotification({ message: "Tableau supprimé.", severity: "success" })
      );
    } catch {
      dispatch(
        showNotification({
          message: "Suppression impossible.",
          severity: "error",
        })
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  if (!tournoi) {
    return (
      <Alert severity="warning">
        Aucun tournoi courant. Définissez un tournoi actif depuis la gestion des
        tournois.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight={700}>
            Définition des tableaux
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tournoi : {tournoi.label} — création, libellés et critères
            d&apos;éligibilité par tableau.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            component={RouterLink}
            to="/admin/boards-managment"
            startIcon={<OpenInNewIcon />}
          >
            Capacité et ouverture
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Ajouter un tableau
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info">
        Pour ajuster les places, ouvrir / fermer un tableau ou la file
        d&apos;attente au quotidien, utilisez{" "}
        <MuiLink component={RouterLink} to="/admin/boards-managment">
          Capacité et ouverture
        </MuiLink>
        .
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Incompat.</TableCell>
              <TableCell align="right">Inscrits / places</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" variant="body2">
                    Aucun tableau. Créez-en un ou importez depuis la console
                    Firebase.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              boards.map((b) => (
                <TableRow key={b.id as string} hover>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{b.label}</TableCell>
                  <TableCell>
                    {b.date instanceof Date
                      ? b.date.toLocaleDateString("fr-FR")
                      : String(b.date)}
                  </TableCell>
                  <TableCell align="center">
                    {(b.incompatibleBoardIds ?? []).length}
                  </TableCell>
                  <TableCell align="right">
                    {b.nbParticipants} / {b.realNbPlaces}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEdit(b)}
                    >
                      Modifier
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setDeleteTarget(b)}
                    >
                      Supprimer
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogMode !== null}
        onClose={() => !submitting && setDialogMode(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {dialogMode === "create"
            ? "Nouveau tableau"
            : "Modifier la définition du tableau"}
        </DialogTitle>
        <DialogContent dividers>
          {dialogMode === "edit" && editingBoard && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Places affichées ({editingBoard.nbPlace}) et réelles (
              {editingBoard.realNbPlaces}) : modifiez-les dans{" "}
              <MuiLink component={RouterLink} to="/admin/boards-managment">
                Capacité et ouverture
              </MuiLink>
              .
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Code (ex. lettre du tableau)"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Libellé affiché aux joueurs"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date du tableau"
                type="date"
                value={form.dateStr}
                onChange={(e) =>
                  setForm((f) => ({ ...f, dateStr: e.target.value }))
                }
                fullWidth
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Heure limite de pointage"
                placeholder="18:30"
                value={form.heureLimite}
                onChange={(e) =>
                  setForm((f) => ({ ...f, heureLimite: e.target.value }))
                }
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  label="Couleur live (blocs joueurs)"
                  value={form.liveColor}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, liveColor: e.target.value }))
                  }
                  placeholder="#e3f2fd"
                  fullWidth
                  size="small"
                  helperText="Affichée sur l'écran live pour ce tableau"
                  InputProps={{
                    startAdornment: (
                      <Box
                        component="input"
                        type="color"
                        value={
                          isValidHexColor(form.liveColor.trim())
                            ? form.liveColor.trim()
                            : "#e3f2fd"
                        }
                        onChange={(e) =>
                          setForm((f) => ({ ...f, liveColor: e.target.value }))
                        }
                        aria-label="Choisir la couleur live"
                        sx={{
                          width: 36,
                          height: 36,
                          p: 0,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "pointer",
                          bgcolor: "transparent",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                    ),
                  }}
                />
              </Stack>
            </Grid>
            {dialogMode === "create" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Places annoncées (affichées)"
                    type="number"
                    value={form.nbPlace}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nbPlace: e.target.value }))
                    }
                    fullWidth
                    required
                    size="small"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Places réelles (capacité)"
                    type="number"
                    value={form.realNbPlaces}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, realNbPlaces: e.target.value }))
                    }
                    fullWidth
                    required
                    size="small"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="sexe-label">Sexe éligible</InputLabel>
                <Select
                  labelId="sexe-label"
                  multiple
                  value={form.sexe}
                  onChange={handleSexeChange}
                  input={<OutlinedInput label="Sexe éligible" />}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {BOARD_SEXE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={form.sexe.includes(o.value)} />
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Catégories d&apos;âge (code licence FFTT)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                En base, on enregistre les codes courts (B1, C2, V55…). Utilisez tout sélectionner
                puis décochez la ou les familles / codes à exclure.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setForm((f) => ({ ...f, cat: getAllBoardCategoryCodes() }))
                  }
                >
                  Tout sélectionner
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setForm((f) => ({ ...f, cat: [] }))}
                >
                  Tout désélectionner
                </Button>
              </Stack>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Par famille (plusieurs codes enregistrés par case)
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {BOARD_CATEGORY_GROUPS.map((g) => {
                  const { checked, indeterminate } = getCategoryGroupCheckboxState(
                    form.cat,
                    g.codes
                  );
                  return (
                    <Grid item xs={12} sm={6} md={4} key={g.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={(_, c) =>
                              handleCategoryGroupToggle(g.codes, c)
                            }
                          />
                        }
                        label={g.labelPlural}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {legacyCategoryCodes.length > 0 && (
                <Alert severity="warning" sx={{ my: 1, py: 0.5 }}>
                  Valeurs non reconnues sur ce tableau :{" "}
                  {legacyCategoryCodes.join(", ")} — remplacez-les par les codes licence ci-dessous
                  ou supprimez-les.
                </Alert>
              )}
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    Sélection précise par code (avancé)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <InputLabel id="cat-label">Codes individuels</InputLabel>
                    <Select
                      labelId="cat-label"
                      multiple
                      value={form.cat}
                      onChange={handleCatChange}
                      input={<OutlinedInput label="Codes individuels" />}
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((c) => formatBoardCategoryMenuLabel(c))
                          .join(", ")
                      }
                    >
                      {categoryCodesForMenu.map((c) => {
                        const isLegacy = !isKnownBoardCategoryCode(c);
                        return (
                          <MenuItem key={c} value={c}>
                            <Checkbox checked={form.cat.includes(c)} />
                            <Typography
                              component="span"
                              variant="body2"
                              color={isLegacy ? "warning.main" : "text.primary"}
                            >
                              {formatBoardCategoryMenuLabel(c)}
                              {isLegacy ? " (ancienne valeur)" : ""}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.handiReserve}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        handiReserve: e.target.checked,
                      }))
                    }
                  />
                }
                label="Tableau réservé handisport"
              />
            </Grid>
            {form.handiReserve && (
              <Grid item xs={12}>
                <TextField
                  label="Classes handisport"
                  helperText="Codes séparés par des virgules (ex. C11, C12)"
                  value={form.classe.join(", ")}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      classe: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Points min (optionnel)"
                type="number"
                value={form.min}
                onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Points max (optionnel)"
                type="number"
                value={form.max}
                onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.obligatoire}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, obligatoire: e.target.checked }))
                    }
                  />
                }
                label="Inscription obligatoire (même journée)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.fullPrice}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, fullPrice: e.target.checked }))
                    }
                  />
                }
                label="Tarif majoré (full price)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.comptePourLimiteJournaliere}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        comptePourLimiteJournaliere: e.target.checked,
                      }))
                    }
                  />
                }
                label="Compte dans la limite de 2 tableaux par jour"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                Décochez pour un tableau qui ne compte pas dans ce plafond (ex. tableau
                additionnel prévu par le règlement).
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.inscriptionSurPlace}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        inscriptionSurPlace: e.target.checked,
                      }))
                    }
                  />
                }
                label="Inscription / constitution sur place"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                Affiche une info sur la page de saisie de licence : ce tableau s’inscrit sur
                place (ex. non licenciées, loisirs par équipes).
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.offert}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        offert: e.target.checked,
                      }))
                    }
                  />
                }
                label="Tableau offert (gratuit)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                N’entre pas dans le calcul du tarif. Si la sélection ne contient que des
                tableaux offerts, pas de passage par Stripe.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Tableaux incompatibles
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Sélectionnez les tableaux qu’un joueur ne peut pas cumuler avec celui-ci. La
                relation est réciproque : si vous cochez le tableau B ici, ce tableau sera aussi
                marqué incompatible avec B.
              </Typography>
              {incompatibleOptions.length === 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Aucun autre tableau pour l’instant. Créez d’autres tableaux puis rouvrez cette
                  fiche pour définir les incompatibilités.
                </Alert>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="incompat-label">Autres tableaux incompatibles</InputLabel>
                  <Select
                    labelId="incompat-label"
                    multiple
                    value={form.incompatibleBoardIds}
                    onChange={handleIncompatibleChange}
                    input={<OutlinedInput label="Autres tableaux incompatibles" />}
                    renderValue={(selected) =>
                      (selected as string[])
                        .map((id) => {
                          const tb = boards.find((x) => x.id === id);
                          return tb ? `${tb.name} — ${tb.label}` : id;
                        })
                        .join(", ")
                    }
                  >
                    {incompatibleOptions.map((b) => (
                      <MenuItem key={b.id as string} value={b.id as string}>
                        <Checkbox
                          checked={form.incompatibleBoardIds.includes(b.id as string)}
                        />
                        <Typography component="span" variant="body2">
                          {b.name} — {b.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() => setDialogMode(null)}
            disabled={submitting}
          >
            Annuler
          </DialogTextActionButton>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting ||
              !form.name.trim() ||
              !form.label.trim() ||
              !form.heureLimite.trim()
            }
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Supprimer ce tableau ?"
        message={
          deleteTarget && (deleteTarget.nbParticipants ?? 0) > 0
            ? `Ce tableau a ${deleteTarget.nbParticipants} inscription(s). La suppression du document Firestore peut laisser des participations orphelines : vérifiez côté base avant de confirmer.`
            : "Confirmer la suppression définitive de ce tableau ?"
        }
        confirmLabel="Supprimer"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Stack>
  );
};

export default AdminBoardDefinitions;
